import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import {
  DollarSign,
  TrendingUp,
  Package,
  CheckCircle,
  Clock,
  XCircle,
  LogOut,
  RefreshCw,
  Loader2,
  Key,
  Copy,
  Eye,
  EyeOff,
  FileText,
  ArrowUpRight,
  Wallet,
  BarChart3,
  ArrowLeft,
  Globe,
  Plus,
  Trash2,
  Code,
  AlertTriangle,
  ExternalLink
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SELLER_PUBLIC_SUBMIT_ENDPOINT = 'https://vitalreachmedia.com/api/seller/leads/submit';
const ANALYTICS_SCRIPT_SNIPPET = `<script>window.ANALYTICS_ENDPOINT='https://vitalreachmedia.com/api/analytics/collect';</script> <script src="https://vitalreachmedia.com/api/analytics/tracker.js" defer></script>`;

export default function SellerDashboardPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [seller, setSeller] = useState(null);
  const [stats, setStats] = useState({
    total_leads_submitted: 0,
    total_leads_sold: 0,
    total_leads_rejected: 0,
    pending_leads: 0,
    balance: 0,
    total_earned: 0
  });
  const [leads, setLeads] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [apiKeyStatus, setApiKeyStatus] = useState({ has_api_key: false, api_keys: [] });
  const [domainApiKeys, setDomainApiKeys] = useState([]);
  const [revealedDomainKeys, setRevealedDomainKeys] = useState({});
  const [showApiKeyByDomain, setShowApiKeyByDomain] = useState({});
  const [generatingDomainKeyId, setGeneratingDomainKeyId] = useState(null);
  
  // Domains state
  const [domains, setDomains] = useState([]);
  const [showAddDomain, setShowAddDomain] = useState(false);
  const [newDomain, setNewDomain] = useState('');
  const [addingDomain, setAddingDomain] = useState(false);
  
  // Integration Calculator state
  const [calcLeadType, setCalcLeadType] = useState('');
  const [calcDomain, setCalcDomain] = useState('');
  const [calcPlatform, setCalcPlatform] = useState('');
  const [generatedCode, setGeneratedCode] = useState('');
  const [showGeneratedCode, setShowGeneratedCode] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('seller_token');
      if (!token) {
        navigate('/seller/login');
        return;
      }

      const headers = { Authorization: `Bearer ${token}` };

      // Fetch seller profile
      const profileRes = await axios.get(`${API_URL}/api/seller/me`, { headers });
      setSeller(profileRes.data);

      // Fetch stats
      const statsRes = await axios.get(`${API_URL}/api/seller/stats`, { headers });
      setStats(statsRes.data);

      // Fetch leads
      const leadsRes = await axios.get(`${API_URL}/api/seller/leads`, { 
        headers,
        params: { limit: 100 }
      });
      setLeads(leadsRes.data.leads || []);

      // Fetch transactions
      const transRes = await axios.get(`${API_URL}/api/seller/transactions`, { headers });
      setTransactions(transRes.data.transactions || []);

      // Fetch API key status
      const apiRes = await axios.get(`${API_URL}/api/seller/api-key`, { headers });
      const apiPayload = apiRes.data || { has_api_key: false, api_keys: [] };
      setApiKeyStatus(apiPayload);
      setDomainApiKeys(apiPayload.api_keys || []);
      
      // Fetch domains
      try {
        const domainsRes = await axios.get(`${API_URL}/api/seller/domains`, { headers });
        setDomains(domainsRes.data.domains || []);
      } catch (e) {
        // Domains endpoint may not exist yet
        setDomains([]);
      }

    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/seller/login');
      } else {
        toast.error('Failed to load dashboard data');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleLogout = () => {
    const isImpersonating = localStorage.getItem('impersonating_seller') === 'true';
    
    if (isImpersonating) {
      const adminToken = localStorage.getItem('admin_return_token');
      localStorage.removeItem('seller_token');
      localStorage.removeItem('seller_data');
      localStorage.removeItem('impersonating_seller');
      localStorage.removeItem('admin_return_token');
      
      if (adminToken) {
        toast.success('Returned to admin view');
        navigate('/admin/sellers');
      } else {
        navigate('/login');
      }
    } else {
      localStorage.removeItem('seller_token');
      localStorage.removeItem('seller_data');
      navigate('/seller/login');
      toast.success('Logged out successfully');
    }
  };

  const isImpersonating = localStorage.getItem('impersonating_seller') === 'true';

  const generateDomainApiKey = async (domainId) => {
    try {
      setGeneratingDomainKeyId(domainId);
      const token = localStorage.getItem('seller_token');
      const res = await axios.post(
        `${API_URL}/api/seller/api-key/generate`,
        { domain_id: domainId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data?.api_key) {
        setRevealedDomainKeys((prev) => ({ ...prev, [domainId]: res.data.api_key }));
        setShowApiKeyByDomain((prev) => ({ ...prev, [domainId]: true }));
      }

      await fetchData();
      toast.success('Domain API key generated successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate domain API key');
    } finally {
      setGeneratingDomainKeyId(null);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const handleAddDomain = async () => {
    if (!newDomain.trim()) {
      toast.error('Please enter a domain');
      return;
    }
    
    try {
      setAddingDomain(true);
      const token = localStorage.getItem('seller_token');
      let cleanDomain = newDomain.trim().toLowerCase();
      cleanDomain = cleanDomain.replace(/^https?:\/\//, '').replace(/\/+$/, '');
      cleanDomain = cleanDomain.split('/')[0];
      cleanDomain = cleanDomain.replace(/^www\./, '');

      if (!cleanDomain) {
        toast.error('Please enter a valid domain (example.com)');
        return;
      }
      
      const response = await axios.post(
        `${API_URL}/api/seller/domains`,
        { domain: cleanDomain },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const createdDomain = response.data?.domain;
      const createdApiKey = response.data?.api_key;
      if (createdDomain?.id && createdApiKey) {
        setRevealedDomainKeys((prev) => ({ ...prev, [createdDomain.id]: createdApiKey }));
        setShowApiKeyByDomain((prev) => ({ ...prev, [createdDomain.id]: true }));
      }
      
      toast.success('Domain added successfully');
      setNewDomain('');
      setShowAddDomain(false);
      fetchData();
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to add domain');
      }
    } finally {
      setAddingDomain(false);
    }
  };

  const handleDeleteDomain = async (domainId) => {
    if (!window.confirm('Are you sure you want to delete this domain?')) return;
    
    try {
      const token = localStorage.getItem('seller_token');
      await axios.delete(
        `${API_URL}/api/seller/domains/${domainId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Domain deleted');
      setDomains(prev => prev.filter(d => d.id !== domainId));
      setDomainApiKeys(prev => prev.filter(k => k.domain_id !== domainId));
      setRevealedDomainKeys(prev => {
        const next = { ...prev };
        delete next[domainId];
        return next;
      });
      setShowApiKeyByDomain(prev => {
        const next = { ...prev };
        delete next[domainId];
        return next;
      });
    } catch (error) {
      toast.error('Failed to delete domain');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-blue-100 text-blue-800',
      sold: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };
    return <Badge className={styles[status] || 'bg-gray-100'}>{status}</Badge>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  const filteredLeads = leads.filter(lead => {
    if (statusFilter === 'all') return true;
    return lead.status === statusFilter;
  });

  const selectedDomainForCode = domains.find(d => d.domain === calcDomain) || null;
  const selectedDomainApiKey = selectedDomainForCode ? revealedDomainKeys[selectedDomainForCode.id] : null;
  const hasAnyDomainKey = Boolean(domainApiKeys?.length);
  const hasApiKeyForGeneration = Boolean(selectedDomainApiKey);

  // Lead type configurations
  const leadTypeConfigs = {
    health: {
      name: 'Health Insurance',
      formTitle: 'Get Your Free Health Insurance Quote',
      buttonText: 'Get My Free Quote',
      additionalFields: ['dob', 'householdSize', 'currentlyInsured'],
      consentText: 'By checking this box and submitting this form, I consent to receive calls, emails, and text messages from Vital Reach Media and its licensed insurance agent partners at the phone number provided above, including calls using an automatic telephone dialing system or pre-recorded voice, for the purpose of discussing health insurance options. I understand that my consent is not a condition of purchase and I may revoke consent at any time. Message and data rates may apply. By submitting this form, I also agree to the Privacy Policy and Terms of Service.'
    },
    medicare: {
      name: 'Medicare',
      formTitle: 'Get Your Free Medicare Quote',
      buttonText: 'Get Medicare Information',
      additionalFields: ['dob', 'medicarePartA', 'medicarePartB'],
      consentText: 'By checking this box and submitting this form, I consent to receive calls, emails, and text messages from Vital Reach Media and its licensed insurance agent partners at the phone number provided above, including calls using an automatic telephone dialing system or pre-recorded voice, for the purpose of discussing Medicare insurance options. I understand that my consent is not a condition of purchase and I may revoke consent at any time. Message and data rates may apply. By submitting this form, I also agree to the Privacy Policy and Terms of Service.'
    },
    auto: {
      name: 'Auto Insurance',
      formTitle: 'Get Your Free Auto Insurance Quote',
      buttonText: 'Get My Auto Quote',
      additionalFields: ['vehicleYear', 'vehicleMake', 'currentlyInsured'],
      consentText: 'By checking this box and submitting this form, I consent to receive calls, emails, and text messages from Vital Reach Media and its licensed insurance agent partners at the phone number provided above, including calls using an automatic telephone dialing system or pre-recorded voice, for the purpose of discussing auto insurance options. I understand that my consent is not a condition of purchase and I may revoke consent at any time. Message and data rates may apply. By submitting this form, I also agree to the Privacy Policy and Terms of Service.'
    },
    life: {
      name: 'Life Insurance',
      formTitle: 'Get Your Free Life Insurance Quote',
      buttonText: 'Get Life Insurance Quote',
      additionalFields: ['dob', 'coverageAmount', 'tobaccoUse'],
      consentText: 'By checking this box and submitting this form, I consent to receive calls, emails, and text messages from Vital Reach Media and its licensed insurance agent partners at the phone number provided above, including calls using an automatic telephone dialing system or pre-recorded voice, for the purpose of discussing life insurance options. I understand that my consent is not a condition of purchase and I may revoke consent at any time. Message and data rates may apply. By submitting this form, I also agree to the Privacy Policy and Terms of Service.'
    }
  };

  // Generate the code based on calculator selections
  const generateIntegrationCode = () => {
    if (!calcLeadType || !calcDomain || !calcPlatform) {
      toast.error('Please select all options');
      return;
    }

    if (!selectedDomainApiKey) {
      toast.error('Generate API key for the selected domain first in API Access tab');
      return;
    }

    const config = leadTypeConfigs[calcLeadType];
    const sellerApiKey = selectedDomainApiKey || 'YOUR_DOMAIN_API_KEY_HERE';
    
    let code = '';
    
    if (calcPlatform === 'html') {
      code = generateHtmlCode(config, calcDomain, sellerApiKey, calcLeadType);
    } else if (calcPlatform === 'wordpress') {
      code = generateWordPressCode(config, calcDomain, sellerApiKey, calcLeadType);
    } else if (calcPlatform === 'joomla') {
      code = generateJoomlaCode(config, calcDomain, sellerApiKey, calcLeadType);
    }
    
    setGeneratedCode(code);
    setShowGeneratedCode(true);
  };

  const generateHtmlCode = (config, domain, apiKey, leadType) => {
    return `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${config.formTitle}</title>
    <script>window.ANALYTICS_ENDPOINT='https://vitalreachmedia.com/api/analytics/collect';</script>
    <script src="https://vitalreachmedia.com/api/analytics/tracker.js" defer></script>
    <style>
        /* Modern Form Styles */
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif; background: #f5f5f5; padding: 20px; }
        
        .vrm-lead-form {
            max-width: 480px;
            margin: 0 auto;
            background: white;
            border-radius: 12px;
            padding: 32px;
            box-shadow: 0 4px 20px rgba(0,0,0,0.1);
        }
        
        .vrm-lead-form h2 {
            text-align: center;
            color: #1a1a2e;
            margin-bottom: 8px;
            font-size: 24px;
        }
        
        .vrm-lead-form .subtitle {
            text-align: center;
            color: #666;
            margin-bottom: 24px;
            font-size: 14px;
        }
        
        .vrm-form-group {
            margin-bottom: 16px;
        }
        
        .vrm-form-group label {
            display: block;
            font-weight: 600;
            color: #333;
            margin-bottom: 6px;
            font-size: 14px;
        }
        
        .vrm-form-group label .required {
            color: #e53e3e;
        }
        
        .vrm-form-group input,
        .vrm-form-group select {
            width: 100%;
            padding: 12px 14px;
            border: 1px solid #ddd;
            border-radius: 8px;
            font-size: 16px;
            transition: border-color 0.2s, box-shadow 0.2s;
        }
        
        .vrm-form-group input:focus,
        .vrm-form-group select:focus {
            outline: none;
            border-color: #10b981;
            box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
        }
        
        .vrm-row {
            display: flex;
            gap: 12px;
        }
        
        .vrm-row .vrm-form-group {
            flex: 1;
        }
        
        .vrm-consent-box {
            background: #f8fafc;
            border: 1px solid #e2e8f0;
            border-radius: 8px;
            padding: 16px;
            margin: 20px 0;
        }
        
        .vrm-consent-box label {
            display: flex;
            align-items: flex-start;
            gap: 10px;
            cursor: pointer;
            font-size: 12px;
            line-height: 1.5;
            color: #4a5568;
        }
        
        .vrm-consent-box input[type="checkbox"] {
            width: 18px;
            height: 18px;
            margin-top: 2px;
            flex-shrink: 0;
        }
        
        .vrm-signature-box {
            background: #fffbeb;
            border: 1px solid #fcd34d;
            border-radius: 8px;
            padding: 16px;
            margin-bottom: 20px;
        }
        
        .vrm-signature-box label {
            font-weight: 600;
            color: #92400e;
            font-size: 13px;
        }
        
        .vrm-signature-box input {
            margin-top: 8px;
            font-style: italic;
        }
        
        .vrm-signature-box .sig-note {
            font-size: 11px;
            color: #92400e;
            margin-top: 6px;
        }
        
        .vrm-submit-btn {
            width: 100%;
            padding: 14px;
            background: linear-gradient(135deg, #10b981, #059669);
            color: white;
            border: none;
            border-radius: 8px;
            font-size: 16px;
            font-weight: 600;
            cursor: pointer;
            transition: transform 0.2s, box-shadow 0.2s;
        }
        
        .vrm-submit-btn:hover {
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4);
        }
        
        .vrm-submit-btn:disabled {
            opacity: 0.7;
            cursor: not-allowed;
            transform: none;
        }
        
        .vrm-privacy-note {
            text-align: center;
            font-size: 11px;
            color: #718096;
            margin-top: 16px;
        }
        
        .vrm-privacy-note a {
            color: #10b981;
        }
    </style>
</head>
<body>
    <form class="vrm-lead-form" id="vrmLeadForm">
        <h2>${config.formTitle}</h2>
        <p class="subtitle">Fill out the form below and we'll get you a quote fast!</p>
        
        <!-- PERSONAL INFORMATION -->
        <div class="vrm-row">
            <div class="vrm-form-group">
                <label>First Name <span class="required">*</span></label>
                <input type="text" name="firstName" placeholder="John" required>
            </div>
            <div class="vrm-form-group">
                <label>Last Name <span class="required">*</span></label>
                <input type="text" name="lastName" placeholder="Smith" required>
            </div>
        </div>
        
        <div class="vrm-form-group">
            <label>Email Address <span class="required">*</span></label>
            <input type="email" name="email" placeholder="john@example.com" required>
        </div>
        
        <div class="vrm-form-group">
            <label>Phone Number <span class="required">*</span></label>
            <input type="tel" name="phone" placeholder="(555) 123-4567" required>
        </div>
        
        <!-- LOCATION -->
        <div class="vrm-row">
            <div class="vrm-form-group">
                <label>State <span class="required">*</span></label>
                <select name="state" required>
                    <option value="">Select State</option>
                    <option value="AL">Alabama</option><option value="AK">Alaska</option><option value="AZ">Arizona</option>
                    <option value="AR">Arkansas</option><option value="CA">California</option><option value="CO">Colorado</option>
                    <option value="CT">Connecticut</option><option value="DE">Delaware</option><option value="FL">Florida</option>
                    <option value="GA">Georgia</option><option value="HI">Hawaii</option><option value="ID">Idaho</option>
                    <option value="IL">Illinois</option><option value="IN">Indiana</option><option value="IA">Iowa</option>
                    <option value="KS">Kansas</option><option value="KY">Kentucky</option><option value="LA">Louisiana</option>
                    <option value="ME">Maine</option><option value="MD">Maryland</option><option value="MA">Massachusetts</option>
                    <option value="MI">Michigan</option><option value="MN">Minnesota</option><option value="MS">Mississippi</option>
                    <option value="MO">Missouri</option><option value="MT">Montana</option><option value="NE">Nebraska</option>
                    <option value="NV">Nevada</option><option value="NH">New Hampshire</option><option value="NJ">New Jersey</option>
                    <option value="NM">New Mexico</option><option value="NY">New York</option><option value="NC">North Carolina</option>
                    <option value="ND">North Dakota</option><option value="OH">Ohio</option><option value="OK">Oklahoma</option>
                    <option value="OR">Oregon</option><option value="PA">Pennsylvania</option><option value="RI">Rhode Island</option>
                    <option value="SC">South Carolina</option><option value="SD">South Dakota</option><option value="TN">Tennessee</option>
                    <option value="TX">Texas</option><option value="UT">Utah</option><option value="VT">Vermont</option>
                    <option value="VA">Virginia</option><option value="WA">Washington</option><option value="WV">West Virginia</option>
                    <option value="WI">Wisconsin</option><option value="WY">Wyoming</option>
                </select>
            </div>
            <div class="vrm-form-group">
                <label>ZIP Code</label>
                <input type="text" name="zipCode" placeholder="12345" maxlength="10">
            </div>
        </div>
        
        <div class="vrm-form-group">
            <label>City</label>
            <input type="text" name="city" placeholder="Miami">
        </div>
        
        ${leadType === 'health' || leadType === 'medicare' || leadType === 'life' ? `
        <!-- DATE OF BIRTH -->
        <div class="vrm-form-group">
            <label>Date of Birth</label>
            <input type="date" name="dob">
        </div>
        ` : ''}
        
        ${leadType === 'auto' ? `
        <!-- VEHICLE INFO -->
        <div class="vrm-row">
            <div class="vrm-form-group">
                <label>Vehicle Year</label>
                <input type="number" name="vehicleYear" placeholder="2022" min="1990" max="2030">
            </div>
            <div class="vrm-form-group">
                <label>Vehicle Make</label>
                <input type="text" name="vehicleMake" placeholder="Toyota">
            </div>
        </div>
        ` : ''}
        
        <!-- HIDDEN FIELDS -->
        <input type="hidden" name="leadType" value="${leadType}">
        <input type="hidden" name="sourceDomain" value="${domain}">
        
        <!-- TCPA CONSENT BOX -->
        <div class="vrm-consent-box">
            <label>
                <input type="checkbox" name="consentTcpa" id="consentTcpa" required>
                <span id="consentLanguage">${config.consentText}</span>
            </label>
        </div>
        
        <!-- ELECTRONIC SIGNATURE -->
        <div class="vrm-signature-box">
            <label>Electronic Signature <span class="required">*</span></label>
            <input type="text" name="electronicSignature" placeholder="Type your full legal name" required>
            <p class="sig-note">By typing your name, you are providing an electronic signature.</p>
        </div>
        
        <button type="submit" class="vrm-submit-btn" id="submitBtn">${config.buttonText}</button>
        
        <p class="vrm-privacy-note">
            Your information is secure. View our <a href="/privacy-policy" target="_blank">Privacy Policy</a>.
        </p>
    </form>

    <script>
    (function() {
        const API_URL = '${SELLER_PUBLIC_SUBMIT_ENDPOINT}';
        const API_KEY = '${apiKey}';
        
        document.getElementById('vrmLeadForm').addEventListener('submit', async function(e) {
            e.preventDefault();
            
            const form = e.target;
            const submitBtn = document.getElementById('submitBtn');
            const originalText = submitBtn.textContent;
            
            // Disable button
            submitBtn.disabled = true;
            submitBtn.textContent = 'Submitting...';
            
            // Collect form data
            const formData = new FormData(form);
            
            // Build lead data object with all TCPA compliance fields
            const leadData = {
                // Personal Info
                firstName: formData.get('firstName'),
                lastName: formData.get('lastName'),
                email: formData.get('email'),
                phone: formData.get('phone'),
                
                // Location
                state: formData.get('state'),
                city: formData.get('city') || null,
                zipCode: formData.get('zipCode') || null,
                
                // Lead Classification
                leadType: formData.get('leadType'),
                insuranceType: '${leadType}',
                
                // Additional Fields
                dob: formData.get('dob') || null,
                vehicleYear: formData.get('vehicleYear') || null,
                vehicleMake: formData.get('vehicleMake') || null,
                
                // Source Tracking
                sourceDomain: formData.get('sourceDomain'),
                sourceUrl: window.location.href,
                landingPage: window.location.href,
                referrer: document.referrer || null,
                
                // TCPA CONSENT - REQUIRED FOR COMPLIANCE
                consentContact: true,
                consentTcpa: formData.get('consentTcpa') === 'on',
                electronicSignature: formData.get('electronicSignature'),
                consentLanguage: document.getElementById('consentLanguage').innerText,
                consentVersion: '1.0',
                
                // Technical Capture - Proves user interaction
                userAgent: navigator.userAgent,
                screenResolution: screen.width + 'x' + screen.height,
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                
                // UTM Tracking
                utmSource: new URLSearchParams(window.location.search).get('utm_source'),
                utmMedium: new URLSearchParams(window.location.search).get('utm_medium'),
                utmCampaign: new URLSearchParams(window.location.search).get('utm_campaign'),
                gclid: new URLSearchParams(window.location.search).get('gclid')
            };
            
            try {
                const response = await fetch(API_URL, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Seller-Key': API_KEY
                    },
                    body: JSON.stringify(leadData)
                });
                
                const result = await response.json();
                
                if (result.success) {
                    // Success! Show thank you message
                    form.innerHTML = '<div style="text-align:center;padding:40px;"><h2 style="color:#10b981;">Thank You!</h2><p style="color:#666;margin-top:10px;">Your request has been submitted successfully.</p><p style="color:#999;font-size:12px;margin-top:20px;">Confirmation: ' + result.confirmation_id + '</p></div>';
                } else {
                    throw new Error(result.detail || 'Submission failed');
                }
            } catch (error) {
                alert('There was an error submitting your request. Please try again or call us directly.');
                submitBtn.disabled = false;
                submitBtn.textContent = originalText;
            }
        });
    })();
    </script>
</body>
</html>`;
  };

  const generateWordPressCode = (config, domain, apiKey, leadType) => {
    return `<?php
/**
 * Vital Reach Media Lead Form - WordPress Plugin
 * 
 * INSTALLATION:
 * 1. Copy this entire code
 * 2. Go to WordPress Admin > Appearance > Theme Editor
 * 3. Open functions.php
 * 4. Paste at the end of the file
 * 5. Save
 * 
 * USAGE:
 * Add shortcode [vrm_${leadType}_form] to any page or post
 */

function vrm_${leadType}_lead_form_shortcode() {
    // Configuration - DO NOT SHARE YOUR API KEY
    $api_key = '${apiKey}';
    $api_url = '${SELLER_PUBLIC_SUBMIT_ENDPOINT}';
    $domain = '${domain}';
    $lead_type = '${leadType}';
    
    // Generate unique form ID for multiple forms on same page
    $form_id = 'vrm_form_' . wp_rand(1000, 9999);
    
    ob_start();
    ?>
    <style>
        .vrm-wp-form { max-width: 480px; margin: 0 auto; background: #fff; border-radius: 12px; padding: 32px; box-shadow: 0 4px 20px rgba(0,0,0,0.1); }
        .vrm-wp-form h3 { text-align: center; color: #1a1a2e; margin-bottom: 8px; font-size: 22px; }
        .vrm-wp-form .vrm-subtitle { text-align: center; color: #666; margin-bottom: 24px; font-size: 14px; }
        .vrm-wp-form .vrm-field { margin-bottom: 16px; }
        .vrm-wp-form .vrm-field label { display: block; font-weight: 600; color: #333; margin-bottom: 6px; font-size: 14px; }
        .vrm-wp-form .vrm-field label .req { color: #e53e3e; }
        .vrm-wp-form .vrm-field input, .vrm-wp-form .vrm-field select { width: 100%; padding: 12px 14px; border: 1px solid #ddd; border-radius: 8px; font-size: 16px; box-sizing: border-box; }
        .vrm-wp-form .vrm-field input:focus, .vrm-wp-form .vrm-field select:focus { outline: none; border-color: #10b981; box-shadow: 0 0 0 3px rgba(16,185,129,0.1); }
        .vrm-wp-form .vrm-row { display: flex; gap: 12px; }
        .vrm-wp-form .vrm-row .vrm-field { flex: 1; }
        .vrm-wp-form .vrm-consent { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 16px; margin: 20px 0; }
        .vrm-wp-form .vrm-consent label { display: flex; align-items: flex-start; gap: 10px; cursor: pointer; font-size: 12px; line-height: 1.5; color: #4a5568; }
        .vrm-wp-form .vrm-consent input[type="checkbox"] { width: 18px; height: 18px; margin-top: 2px; flex-shrink: 0; }
        .vrm-wp-form .vrm-signature { background: #fffbeb; border: 1px solid #fcd34d; border-radius: 8px; padding: 16px; margin-bottom: 20px; }
        .vrm-wp-form .vrm-signature label { font-weight: 600; color: #92400e; font-size: 13px; }
        .vrm-wp-form .vrm-signature input { margin-top: 8px; font-style: italic; }
        .vrm-wp-form .vrm-signature .note { font-size: 11px; color: #92400e; margin-top: 6px; }
        .vrm-wp-form .vrm-submit { width: 100%; padding: 14px; background: linear-gradient(135deg, #10b981, #059669); color: white; border: none; border-radius: 8px; font-size: 16px; font-weight: 600; cursor: pointer; }
        .vrm-wp-form .vrm-submit:hover { box-shadow: 0 4px 12px rgba(16,185,129,0.4); }
        .vrm-wp-form .vrm-submit:disabled { opacity: 0.7; cursor: not-allowed; }
    </style>
    
    <form class="vrm-wp-form" id="<?php echo $form_id; ?>">
        <h3>${config.formTitle}</h3>
        <p class="vrm-subtitle">Fill out the form below for a free quote!</p>
        
        <div class="vrm-row">
            <div class="vrm-field">
                <label>First Name <span class="req">*</span></label>
                <input type="text" name="firstName" required>
            </div>
            <div class="vrm-field">
                <label>Last Name <span class="req">*</span></label>
                <input type="text" name="lastName" required>
            </div>
        </div>
        
        <div class="vrm-field">
            <label>Email <span class="req">*</span></label>
            <input type="email" name="email" required>
        </div>
        
        <div class="vrm-field">
            <label>Phone <span class="req">*</span></label>
            <input type="tel" name="phone" required>
        </div>
        
        <div class="vrm-row">
            <div class="vrm-field">
                <label>State <span class="req">*</span></label>
                <select name="state" required>
                    <option value="">Select</option>
                    <?php
                    $states = array('AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY');
                    foreach($states as $st) { echo '<option value="'.$st.'">'.$st.'</option>'; }
                    ?>
                </select>
            </div>
            <div class="vrm-field">
                <label>ZIP Code</label>
                <input type="text" name="zipCode" maxlength="10">
            </div>
        </div>
        
        <div class="vrm-consent">
            <label>
                <input type="checkbox" name="consentTcpa" id="consent_<?php echo $form_id; ?>" required>
                <span id="consent_text_<?php echo $form_id; ?>">${config.consentText}</span>
            </label>
        </div>
        
        <div class="vrm-signature">
            <label>Electronic Signature <span class="req">*</span></label>
            <input type="text" name="electronicSignature" placeholder="Type your full legal name" required>
            <p class="note">By typing your name, you provide an electronic signature.</p>
        </div>
        
        <button type="submit" class="vrm-submit">${config.buttonText}</button>
    </form>
    
    <script>window.ANALYTICS_ENDPOINT='https://vitalreachmedia.com/api/analytics/collect';</script>
    <script src="https://vitalreachmedia.com/api/analytics/tracker.js" defer></script>
    <script>
    document.getElementById('<?php echo $form_id; ?>').addEventListener('submit', async function(e) {
        e.preventDefault();
        var form = e.target;
        var btn = form.querySelector('.vrm-submit');
        btn.disabled = true;
        btn.textContent = 'Submitting...';
        
        var leadData = {
            firstName: form.firstName.value,
            lastName: form.lastName.value,
            email: form.email.value,
            phone: form.phone.value,
            state: form.state.value,
            zipCode: form.zipCode.value || null,
            leadType: '<?php echo $lead_type; ?>',
            insuranceType: '<?php echo $lead_type; ?>',
            sourceDomain: '<?php echo $domain; ?>',
            sourceUrl: window.location.href,
            consentContact: true,
            consentTcpa: form.consentTcpa.checked,
            electronicSignature: form.electronicSignature.value,
            consentLanguage: document.getElementById('consent_text_<?php echo $form_id; ?>').innerText,
            userAgent: navigator.userAgent,
            screenResolution: screen.width + 'x' + screen.height,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
        };
        
        try {
            var response = await fetch('<?php echo $api_url; ?>', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-Seller-Key': '<?php echo $api_key; ?>' },
                body: JSON.stringify(leadData)
            });
            var result = await response.json();
            if (result.success) {
                form.innerHTML = '<div style="text-align:center;padding:30px;"><h3 style="color:#10b981;">Thank You!</h3><p>Confirmation: ' + result.confirmation_id + '</p></div>';
            } else { throw new Error(result.detail); }
        } catch (error) {
            alert('Error submitting. Please try again.');
            btn.disabled = false;
            btn.textContent = '${config.buttonText}';
        }
    });
    </script>
    <?php
    return ob_get_clean();
}
add_shortcode('vrm_${leadType}_form', 'vrm_${leadType}_lead_form_shortcode');

/* 
 * USAGE INSTRUCTIONS:
 * 
 * 1. Add this code to your theme's functions.php
 * 2. Use the shortcode [vrm_${leadType}_form] on any page
 * 3. The form will automatically capture all required TCPA consent data
 * 
 * REQUIRED FIELDS CAPTURED:
 * - First Name, Last Name, Email, Phone, State
 * - TCPA Consent checkbox (legally required)
 * - Electronic Signature (legally required)
 * - Consent Language (exact text shown to user)
 * - IP Address, User Agent, Timestamp (for audit trail)
 */
?>`;
  };

  const generateJoomlaCode = (config, domain, apiKey, leadType) => {
    return `<?php
/**
 * Vital Reach Media Lead Form - Joomla Module
 * 
 * INSTALLATION:
 * 1. Create folder: modules/mod_vrm_leadform/
 * 2. Save this file as: modules/mod_vrm_leadform/mod_vrm_leadform.php
 * 3. Create mod_vrm_leadform.xml (manifest) in the same folder
 * 4. Go to Extensions > Manage > Discover
 * 5. Click "Discover" then install the module
 * 6. Go to Extensions > Modules, enable and assign to a position
 */

defined('_JEXEC') or die;

// Configuration
$api_key = '${apiKey}';
$api_url = '${SELLER_PUBLIC_SUBMIT_ENDPOINT}';
$domain = '${domain}';
$lead_type = '${leadType}';
$module_id = $module->id;
?>

<style>
    .vrm-joomla-form { max-width: 480px; margin: 0 auto; background: #fff; border-radius: 12px; padding: 32px; box-shadow: 0 4px 20px rgba(0,0,0,0.1); }
    .vrm-joomla-form h3 { text-align: center; color: #1a1a2e; margin-bottom: 24px; }
    .vrm-joomla-form .field { margin-bottom: 16px; }
    .vrm-joomla-form .field label { display: block; font-weight: 600; margin-bottom: 6px; font-size: 14px; }
    .vrm-joomla-form .field input, .vrm-joomla-form .field select { width: 100%; padding: 12px; border: 1px solid #ddd; border-radius: 8px; font-size: 16px; box-sizing: border-box; }
    .vrm-joomla-form .consent { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 16px; margin: 20px 0; font-size: 12px; }
    .vrm-joomla-form .consent label { display: flex; align-items: flex-start; gap: 10px; }
    .vrm-joomla-form .signature { background: #fffbeb; border: 1px solid #fcd34d; border-radius: 8px; padding: 16px; margin-bottom: 20px; }
    .vrm-joomla-form button { width: 100%; padding: 14px; background: #10b981; color: white; border: none; border-radius: 8px; font-size: 16px; font-weight: 600; cursor: pointer; }
</style>

<form class="vrm-joomla-form" id="vrm_form_<?php echo $module_id; ?>">
    <h3>${config.formTitle}</h3>
    
    <div class="field">
        <label>First Name *</label>
        <input type="text" name="firstName" required>
    </div>
    <div class="field">
        <label>Last Name *</label>
        <input type="text" name="lastName" required>
    </div>
    <div class="field">
        <label>Email *</label>
        <input type="email" name="email" required>
    </div>
    <div class="field">
        <label>Phone *</label>
        <input type="tel" name="phone" required>
    </div>
    <div class="field">
        <label>State *</label>
        <select name="state" required>
            <option value="">Select State</option>
            <?php
            $states = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];
            foreach($states as $st) { echo '<option value="'.$st.'">'.$st.'</option>'; }
            ?>
        </select>
    </div>
    
    <div class="consent">
        <label>
            <input type="checkbox" name="consentTcpa" id="consent_<?php echo $module_id; ?>" required>
            <span id="consent_text_<?php echo $module_id; ?>">${config.consentText}</span>
        </label>
    </div>
    
    <div class="signature">
        <label>Electronic Signature *</label>
        <input type="text" name="electronicSignature" placeholder="Type your full legal name" required>
        <p style="font-size:11px;color:#92400e;margin-top:6px;">By typing your name, you provide an electronic signature.</p>
    </div>
    
    <button type="submit">${config.buttonText}</button>
</form>

<script>window.ANALYTICS_ENDPOINT='https://vitalreachmedia.com/api/analytics/collect';</script>
<script src="https://vitalreachmedia.com/api/analytics/tracker.js" defer></script>
<script>
document.getElementById('vrm_form_<?php echo $module_id; ?>').addEventListener('submit', async function(e) {
    e.preventDefault();
    var form = e.target;
    var btn = form.querySelector('button');
    btn.disabled = true;
    btn.textContent = 'Submitting...';
    
    var leadData = {
        firstName: form.firstName.value,
        lastName: form.lastName.value,
        email: form.email.value,
        phone: form.phone.value,
        state: form.state.value,
        leadType: '<?php echo $lead_type; ?>',
        sourceDomain: '<?php echo $domain; ?>',
        sourceUrl: window.location.href,
        consentContact: true,
        consentTcpa: form.consentTcpa.checked,
        electronicSignature: form.electronicSignature.value,
        consentLanguage: document.getElementById('consent_text_<?php echo $module_id; ?>').innerText,
        userAgent: navigator.userAgent,
        screenResolution: screen.width + 'x' + screen.height,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    };
    
    try {
        var response = await fetch('<?php echo $api_url; ?>', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-Seller-Key': '<?php echo $api_key; ?>' },
            body: JSON.stringify(leadData)
        });
        var result = await response.json();
        if (result.success) {
            form.innerHTML = '<div style="text-align:center;padding:30px;"><h3 style="color:#10b981;">Thank You!</h3><p>Confirmation: ' + result.confirmation_id + '</p></div>';
        } else { throw new Error(result.detail); }
    } catch (error) {
        alert('Error submitting. Please try again.');
        btn.disabled = false;
        btn.textContent = '${config.buttonText}';
    }
});
</script>

<?php
/*
XML MANIFEST FILE - Save as mod_vrm_leadform.xml:

<?xml version="1.0" encoding="utf-8"?>
<extension type="module" version="4.0" client="site" method="upgrade">
    <name>VRM Lead Form</name>
    <author>Vital Reach Media</author>
    <version>1.0.0</version>
    <description>Lead capture form with TCPA compliance</description>
    <files>
        <filename module="mod_vrm_leadform">mod_vrm_leadform.php</filename>
    </files>
</extension>
*/
?>`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center" data-testid="seller-dashboard-loading">
        <Loader2 className="w-8 h-8 animate-spin text-green-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900" data-testid="seller-dashboard-page">
      {/* Impersonation Banner */}
      {isImpersonating && (
        <div className="bg-purple-600 text-white py-2 px-4 text-center text-sm">
          <span className="font-medium">Admin View:</span> You are viewing this seller's dashboard.{' '}
          <button data-testid="seller-exit-impersonation-button" onClick={handleLogout} className="underline hover:no-underline font-medium">
            Exit Impersonation
          </button>
        </div>
      )}
      
      {/* Header */}
      <header className="bg-black/40 backdrop-blur-xl border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-white font-bold">Seller Portal</h1>
                <p className="text-gray-400 text-xs">{seller?.company_name}</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Button data-testid="seller-dashboard-refresh-button" variant="ghost" size="sm" onClick={fetchData} className="text-gray-400 hover:text-white">
                <RefreshCw className="w-4 h-4" />
              </Button>
              <Button data-testid="seller-dashboard-logout-button" variant="ghost" size="sm" onClick={handleLogout} className="text-gray-400 hover:text-white">
                {isImpersonating ? (
                  <><ArrowLeft className="w-4 h-4 mr-2" />Exit</>
                ) : (
                  <><LogOut className="w-4 h-4 mr-2" />Logout</>
                )}
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Balance Card */}
        <Card className="bg-gradient-to-r from-green-600 to-emerald-700 border-0 mb-8">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm mb-1">Available Balance</p>
                <p className="text-4xl font-bold text-white">${stats.balance?.toFixed(2) || '0.00'}</p>
                <p className="text-green-200 text-sm mt-1">Total Earned: ${stats.total_earned?.toFixed(2) || '0.00'}</p>
              </div>
              <Wallet className="w-16 h-16 text-green-300/50" />
            </div>
          </CardContent>
        </Card>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-black/40 border-white/10">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <Package className="w-8 h-8 text-blue-400" />
                <div>
                  <p className="text-2xl font-bold text-white">{stats.total_leads_submitted || 0}</p>
                  <p className="text-xs text-gray-400">Total Submitted</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-black/40 border-white/10">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-8 h-8 text-green-400" />
                <div>
                  <p className="text-2xl font-bold text-white">{stats.total_leads_sold || 0}</p>
                  <p className="text-xs text-gray-400">Sold</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-black/40 border-white/10">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <Clock className="w-8 h-8 text-yellow-400" />
                <div>
                  <p className="text-2xl font-bold text-white">{stats.pending_leads || 0}</p>
                  <p className="text-xs text-gray-400">Pending</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-black/40 border-white/10">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <TrendingUp className="w-8 h-8 text-purple-400" />
                <div>
                  <p className="text-2xl font-bold text-white">
                    {stats.total_leads_submitted > 0 
                      ? ((stats.total_leads_sold / stats.total_leads_submitted) * 100).toFixed(0)
                      : 0}%
                  </p>
                  <p className="text-xs text-gray-400">Sell Rate</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="leads" className="space-y-6" data-testid="seller-dashboard-tabs">
          <TabsList className="bg-black/40 border border-white/10">
            <TabsTrigger value="leads" data-testid="seller-tab-leads">My Leads</TabsTrigger>
            <TabsTrigger value="transactions" data-testid="seller-tab-transactions">Transactions</TabsTrigger>
            <TabsTrigger value="domains" data-testid="seller-tab-domains">Domains</TabsTrigger>
            <TabsTrigger value="api" data-testid="seller-tab-api">API Access</TabsTrigger>
            <TabsTrigger value="integration" data-testid="seller-tab-integration">Integration Guide</TabsTrigger>
          </TabsList>

          {/* Leads Tab */}
          <TabsContent value="leads">
            <Card className="bg-black/40 border-white/10">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-white">My Leads</CardTitle>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger data-testid="seller-leads-status-filter" className="w-[140px] bg-white/5 border-white/10 text-white">
                      <SelectValue placeholder="Filter" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="sold">Sold</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10">
                      <TableHead className="text-gray-400">Confirmation</TableHead>
                      <TableHead className="text-gray-400">Type</TableHead>
                      <TableHead className="text-gray-400">Location</TableHead>
                      <TableHead className="text-gray-400">Status</TableHead>
                      <TableHead className="text-gray-400">Payout</TableHead>
                      <TableHead className="text-gray-400">Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredLeads.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-gray-400 py-8">
                          No leads found
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredLeads.map((lead) => (
                        <TableRow key={lead.id} className="border-white/10">
                          <TableCell className="text-white font-mono text-sm">{lead.confirmation_id}</TableCell>
                          <TableCell><Badge variant="outline" className="text-gray-300">{lead.lead_type}</Badge></TableCell>
                          <TableCell className="text-gray-300">{lead.city && `${lead.city}, `}{lead.state}</TableCell>
                          <TableCell>{getStatusBadge(lead.status)}</TableCell>
                          <TableCell className="text-green-400">${lead.payment_rate?.toFixed(2) || '0.00'}</TableCell>
                          <TableCell className="text-gray-400">{formatDate(lead.created_at)}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Transactions Tab */}
          <TabsContent value="transactions">
            <Card className="bg-black/40 border-white/10">
              <CardHeader>
                <CardTitle className="text-white">Transaction History</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10">
                      <TableHead className="text-gray-400">Date</TableHead>
                      <TableHead className="text-gray-400">Type</TableHead>
                      <TableHead className="text-gray-400">Description</TableHead>
                      <TableHead className="text-gray-400">Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {transactions.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center text-gray-400 py-8">
                          No transactions yet
                        </TableCell>
                      </TableRow>
                    ) : (
                      transactions.map((tx) => (
                        <TableRow key={tx.id} className="border-white/10">
                          <TableCell className="text-gray-300">{formatDate(tx.created_at)}</TableCell>
                          <TableCell>
                            <Badge className={tx.type === 'credit' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}>
                              {tx.type}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-gray-300">{tx.description}</TableCell>
                          <TableCell className={tx.type === 'credit' ? 'text-green-400' : 'text-red-400'}>
                            {tx.type === 'credit' ? '+' : '-'}${Math.abs(tx.amount).toFixed(2)}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Domains Tab */}
          <TabsContent value="domains">
            <Card className="bg-black/40 border-white/10">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-white">My Domains</CardTitle>
                    <CardDescription className="text-gray-400">
                      Register the domains where you'll submit leads from
                    </CardDescription>
                  </div>
                  <Button data-testid="seller-add-domain-open-button" onClick={() => setShowAddDomain(true)} className="bg-green-600 hover:bg-green-700">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Domain
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {domains.length === 0 ? (
                  <div className="text-center py-8 text-gray-400">
                    <Globe className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No domains registered yet</p>
                    <p className="text-sm">Add a domain to start submitting leads</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow className="border-white/10">
                        <TableHead className="text-gray-400">Domain</TableHead>
                        <TableHead className="text-gray-400">API Key Prefix</TableHead>
                        <TableHead className="text-gray-400">Analytics Code</TableHead>
                        <TableHead className="text-gray-400">Leads</TableHead>
                        <TableHead className="text-gray-400">Added</TableHead>
                        <TableHead className="text-gray-400">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {domains.map((domain) => (
                        <TableRow key={domain.id} className="border-white/10">
                          <TableCell>
                            <div className="flex items-center gap-2 text-white">
                              <Globe className="w-4 h-4 text-green-400" />
                              {domain.domain}
                            </div>
                          </TableCell>
                          <TableCell className="text-gray-300 font-mono text-xs">
                            {domain.api_key_prefix ? `${domain.api_key_prefix}...` : 'No key yet'}
                          </TableCell>
                          <TableCell>
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-white/20 text-gray-300"
                              data-testid={`seller-copy-analytics-code-${domain.id}`}
                              onClick={() => copyToClipboard(domain.analytics_code || ANALYTICS_SCRIPT_SNIPPET)}
                            >
                              <Copy className="w-3 h-3 mr-2" />
                              Copy Code
                            </Button>
                          </TableCell>
                          <TableCell className="text-gray-300">{domain.lead_count || 0}</TableCell>
                          <TableCell className="text-gray-400">{formatDate(domain.created_at)}</TableCell>
                          <TableCell>
                            <Button data-testid={`seller-delete-domain-${domain.id}`} size="sm" variant="ghost" className="text-red-400 hover:text-red-300" onClick={() => handleDeleteDomain(domain.id)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* API Access Tab */}
          <TabsContent value="api">
            <Card className="bg-black/40 border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Key className="w-5 h-5 text-green-400" />
                  API Access
                </CardTitle>
                <CardDescription className="text-gray-400">
                  Your API key for submitting leads programmatically
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {domains.length === 0 ? (
                  <div className="text-center py-8">
                    <Globe className="w-12 h-12 mx-auto mb-3 text-gray-500" />
                    <p className="text-gray-400 mb-2">Add a domain first</p>
                    <p className="text-gray-500 text-sm">Each domain gets exactly one API key.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                        <div className="text-sm">
                          <p className="text-yellow-200 font-medium">One API key per domain (synced)</p>
                          <p className="text-yellow-200/70">Deleting a domain deletes its API key automatically.</p>
                        </div>
                      </div>
                    </div>

                    <Table>
                      <TableHeader>
                        <TableRow className="border-white/10">
                          <TableHead className="text-gray-400">Domain</TableHead>
                          <TableHead className="text-gray-400">Key Prefix</TableHead>
                          <TableHead className="text-gray-400">API Key</TableHead>
                          <TableHead className="text-gray-400">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {domains.map((domain) => {
                          const keyMeta = domainApiKeys.find((k) => k.domain_id === domain.id);
                          const revealedKey = revealedDomainKeys[domain.id];
                          const showKey = Boolean(showApiKeyByDomain[domain.id]);

                          return (
                            <TableRow key={domain.id} className="border-white/10">
                              <TableCell className="text-white">{domain.domain}</TableCell>
                              <TableCell className="text-gray-300 font-mono text-xs">
                                {keyMeta?.key_prefix ? `${keyMeta.key_prefix}...` : 'No key'}
                              </TableCell>
                              <TableCell>
                                <code className="block bg-black/40 px-3 py-2 rounded-lg text-green-400 font-mono text-xs">
                                  {revealedKey
                                    ? (showKey ? revealedKey : '•'.repeat(38))
                                    : 'Not viewable yet — click Generate/Rotate'}
                                </code>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <Button
                                    data-testid={`seller-generate-api-key-${domain.id}`}
                                    size="sm"
                                    onClick={() => generateDomainApiKey(domain.id)}
                                    disabled={generatingDomainKeyId === domain.id}
                                    className="bg-green-600 hover:bg-green-700"
                                  >
                                    {generatingDomainKeyId === domain.id ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Key className="w-4 h-4 mr-2" />}
                                    {revealedKey ? 'Rotate' : 'Generate'}
                                  </Button>
                                  {revealedKey && (
                                    <>
                                      <Button
                                        data-testid={`seller-toggle-api-key-${domain.id}`}
                                        size="sm"
                                        variant="ghost"
                                        className="text-gray-300"
                                        onClick={() => setShowApiKeyByDomain((prev) => ({ ...prev, [domain.id]: !prev[domain.id] }))}
                                      >
                                        {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                      </Button>
                                      <Button
                                        data-testid={`seller-copy-api-key-${domain.id}`}
                                        size="sm"
                                        variant="ghost"
                                        className="text-gray-300"
                                        onClick={() => copyToClipboard(revealedKey)}
                                      >
                                        <Copy className="w-4 h-4" />
                                      </Button>
                                    </>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>

                    <div>
                      <Label className="text-gray-400 text-sm">API Endpoint</Label>
                      <div className="flex items-center gap-2 mt-2">
                        <code className="flex-1 bg-black/40 px-4 py-3 rounded-lg text-gray-300 font-mono text-sm">
                          POST {SELLER_PUBLIC_SUBMIT_ENDPOINT}
                        </code>
                        <Button data-testid="seller-copy-endpoint-button" size="sm" variant="ghost" onClick={() => copyToClipboard(SELLER_PUBLIC_SUBMIT_ENDPOINT)} className="text-gray-400">
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Payment Rates */}
                <div className="border-t border-white/10 pt-6">
                  <h4 className="text-white font-medium mb-4">Payment Rates</h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="p-4 bg-white/5 rounded-lg text-center">
                      <p className="text-gray-400 text-sm">Cold Call</p>
                      <p className="text-2xl font-bold text-green-400">${seller?.payment_rates?.cold_call?.toFixed(2) || '0.00'}</p>
                    </div>
                    <div className="p-4 bg-white/5 rounded-lg text-center">
                      <p className="text-gray-400 text-sm">Live Transfer</p>
                      <p className="text-2xl font-bold text-green-400">${seller?.payment_rates?.live_transfer?.toFixed(2) || '0.00'}</p>
                    </div>
                    <div className="p-4 bg-white/5 rounded-lg text-center">
                      <p className="text-gray-400 text-sm">Data Lead</p>
                      <p className="text-2xl font-bold text-green-400">${seller?.payment_rates?.data_lead?.toFixed(2) || '0.00'}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Integration Guide Tab */}
          <TabsContent value="integration">
            <div className="space-y-6">
              {/* Code Calculator */}
              <Card className="bg-black/40 border-white/10">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Code className="w-5 h-5 text-teal-400" />
                    Form Code Generator
                  </CardTitle>
                  <CardDescription className="text-gray-400">
                    Select your options below to generate ready-to-use form code
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Prerequisites Check */}
                  <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                    <h3 className="font-semibold text-yellow-300 mb-2 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      Before You Begin
                    </h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2 text-yellow-200/80">
                        {hasAnyDomainKey ? <CheckCircle className="w-4 h-4 text-green-400" /> : <XCircle className="w-4 h-4 text-red-400" />}
                        <span>{hasAnyDomainKey ? 'Domain API keys configured' : 'Generate API key(s) in the "API Access" tab'}</span>
                      </div>
                      <div className="flex items-center gap-2 text-yellow-200/80">
                        {domains.length > 0 ? <CheckCircle className="w-4 h-4 text-green-400" /> : <XCircle className="w-4 h-4 text-red-400" />}
                        <span>{domains.length > 0 ? `${domains.length} domain(s) registered` : 'Add your domain in the "Domains" tab'}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Calculator Options */}
                  <div className="grid md:grid-cols-3 gap-4">
                    {/* Lead Type */}
                    <div>
                      <Label className="text-gray-300 mb-2 block">1. Lead Type</Label>
                      <Select value={calcLeadType} onValueChange={setCalcLeadType}>
                        <SelectTrigger data-testid="seller-integration-lead-type-select" className="bg-white/5 border-white/10 text-white">
                          <SelectValue placeholder="Select lead type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="health">Health Insurance</SelectItem>
                          <SelectItem value="medicare">Medicare</SelectItem>
                          <SelectItem value="auto">Auto Insurance</SelectItem>
                          <SelectItem value="life">Life Insurance</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Domain */}
                    <div>
                      <Label className="text-gray-300 mb-2 block">2. Domain</Label>
                      <Select value={calcDomain} onValueChange={setCalcDomain} disabled={domains.length === 0}>
                        <SelectTrigger data-testid="seller-integration-domain-select" className="bg-white/5 border-white/10 text-white">
                          <SelectValue placeholder={domains.length === 0 ? 'Add domain first' : 'Select domain'} />
                        </SelectTrigger>
                        <SelectContent>
                          {domains.map(d => (
                            <SelectItem key={d.id} value={d.domain}>{d.domain}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Platform */}
                    <div>
                      <Label className="text-gray-300 mb-2 block">3. Platform</Label>
                      <Select value={calcPlatform} onValueChange={setCalcPlatform}>
                        <SelectTrigger data-testid="seller-integration-platform-select" className="bg-white/5 border-white/10 text-white">
                          <SelectValue placeholder="Select platform" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="html">HTML (Any Website)</SelectItem>
                          <SelectItem value="wordpress">WordPress</SelectItem>
                          <SelectItem value="joomla">Joomla</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  {/* Generate Button */}
                  <Button 
                    data-testid="seller-generate-integration-code-button"
                    onClick={generateIntegrationCode}
                    disabled={!calcLeadType || !calcDomain || !calcPlatform || !hasApiKeyForGeneration}
                    className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white py-6 text-lg"
                  >
                    <Code className="w-5 h-5 mr-2" />
                    Generate Form Code
                  </Button>
                  {calcDomain && !hasApiKeyForGeneration && (
                    <p className="text-xs text-red-300" data-testid="seller-integration-missing-domain-key-note">
                      Generate/rotate API key for selected domain in API Access tab.
                    </p>
                  )}
                  
                  {/* Generated Code Display */}
                  {showGeneratedCode && generatedCode && (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h4 className="text-white font-medium flex items-center gap-2">
                          <CheckCircle className="w-5 h-5 text-green-400" />
                          Your Code is Ready!
                        </h4>
                        <Button 
                          data-testid="seller-copy-generated-code-button"
                          onClick={() => copyToClipboard(generatedCode)}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <Copy className="w-4 h-4 mr-2" />
                          Copy Code
                        </Button>
                      </div>
                      
                      <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto max-h-[400px] overflow-y-auto" data-testid="seller-generated-code-container">
                        <pre className="text-xs text-gray-300 whitespace-pre-wrap font-mono">
                          {generatedCode}
                        </pre>
                      </div>
                      
                      {/* Installation Instructions */}
                      <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                        <h4 className="text-blue-300 font-medium mb-2">Installation Instructions</h4>
                        {calcPlatform === 'html' && (
                          <ol className="text-sm text-blue-200/80 space-y-1 list-decimal list-inside">
                            <li>Copy the entire code above</li>
                            <li>Create a new .html file or paste into your existing page</li>
                            <li>Save and upload to your web server</li>
                            <li>Test the form to ensure leads are being submitted</li>
                          </ol>
                        )}
                        {calcPlatform === 'wordpress' && (
                          <ol className="text-sm text-blue-200/80 space-y-1 list-decimal list-inside">
                            <li>Go to WordPress Admin → Appearance → Theme Editor</li>
                            <li>Open your theme's functions.php file</li>
                            <li>Paste the code at the end of the file and Save</li>
                            <li>Use shortcode <code className="bg-blue-900/50 px-1 rounded">[vrm_{calcLeadType}_form]</code> on any page</li>
                          </ol>
                        )}
                        {calcPlatform === 'joomla' && (
                          <ol className="text-sm text-blue-200/80 space-y-1 list-decimal list-inside">
                            <li>Create folder: modules/mod_vrm_leadform/</li>
                            <li>Save the code as mod_vrm_leadform.php</li>
                            <li>Create the XML manifest file (see comments in code)</li>
                            <li>Go to Extensions → Discover → Install</li>
                          </ol>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
              
              {/* What's Included */}
              <Card className="bg-black/40 border-white/10">
                <CardHeader>
                  <CardTitle className="text-white">What's Included in the Generated Form</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="text-green-400 font-medium mb-3">Required Fields</h4>
                      <ul className="space-y-2 text-sm text-gray-300">
                        <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> First Name & Last Name</li>
                        <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> Email Address</li>
                        <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> Phone Number</li>
                        <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> State (all 50 states)</li>
                        <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> ZIP Code & City</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="text-blue-400 font-medium mb-3">TCPA Compliance (Automatic)</h4>
                      <ul className="space-y-2 text-sm text-gray-300">
                        <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-blue-400" /> TCPA Consent Checkbox</li>
                        <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-blue-400" /> Proper Consent Language</li>
                        <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-blue-400" /> Electronic Signature</li>
                        <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-blue-400" /> IP Address & Timestamp</li>
                        <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-blue-400" /> User Agent & Screen Size</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* API Reference */}
              <Card className="bg-black/40 border-white/10">
                <CardHeader>
                  <CardTitle className="text-white">API Reference (For Developers)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <Label className="text-gray-400 text-sm">Endpoint</Label>
                      <code className="block bg-gray-900 px-4 py-3 rounded-lg text-green-400 font-mono text-sm mt-1">
                        POST {SELLER_PUBLIC_SUBMIT_ENDPOINT}
                      </code>
                    </div>
                    <div>
                      <Label className="text-gray-400 text-sm">Required Header</Label>
                      <code className="block bg-gray-900 px-4 py-3 rounded-lg text-gray-300 font-mono text-sm mt-1">
                        X-Seller-Key: {selectedDomainApiKey ? selectedDomainApiKey.substring(0, 20) + '...' : 'your_domain_api_key'}
                      </code>
                    </div>
                    <div>
                      <Label className="text-gray-400 text-sm">Required Fields</Label>
                      <div className="bg-gray-900 rounded-lg p-4 mt-1">
                        <pre className="text-xs text-gray-300 font-mono">{`{
  "firstName": "string",     // Required
  "lastName": "string",      // Required  
  "email": "string",         // Required
  "phone": "string",         // Required
  "state": "string",         // Required (2-letter code)
  "leadType": "string",      // health, medicare, auto, life
  "consentTcpa": true,       // Required for compliance
  "electronicSignature": "string",  // Required
  "consentLanguage": "string"       // Full text shown to user
}`}</pre>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Add Domain Dialog */}
      <Dialog open={showAddDomain} onOpenChange={setShowAddDomain}>
        <DialogContent className="bg-gray-900 border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">Add Domain</DialogTitle>
            <DialogDescription className="text-gray-400">
              Register a domain where you'll submit leads from
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-gray-300">Domain Name</Label>
              <Input
                data-testid="seller-add-domain-input"
                value={newDomain}
                onChange={(e) => setNewDomain(e.target.value)}
                placeholder="example.com"
                className="bg-white/5 border-white/10 text-white"
              />
              <p className="text-xs text-gray-500 mt-1">Enter without http:// or https://</p>
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="seller-add-domain-cancel-button" variant="outline" onClick={() => setShowAddDomain(false)} className="border-white/20 text-gray-300">
              Cancel
            </Button>
            <Button data-testid="seller-add-domain-submit-button" onClick={handleAddDomain} disabled={addingDomain} className="bg-green-600 hover:bg-green-700">
              {addingDomain ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Add Domain
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
